import { useMemo, type ReactNode } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

function stripMatchingTitle(markdown: string, title: string): string {
  const trimmed = markdown.replace(/^\s+/, "")
  const lines = trimmed.split("\n")
  const first = lines[0]?.replace(/^#{1,6}\s+/, "").trim() ?? ""
  if (first && first.toLowerCase() === title.trim().toLowerCase()) {
    return lines.slice(1).join("\n").replace(/^\s+/, "")
  }
  return trimmed
}

function textOnly({ children }: { children?: ReactNode }) {
  return <span>{children}</span>
}

/**
 * Compact rendered body for a folder grid tile.
 * Links and images stay inert so the tile click opens the document.
 */
export function DocumentTileMarkdown({
  source,
  title,
}: {
  source: string
  title: string
}) {
  const body = useMemo(() => stripMatchingTitle(source, title), [source, title])
  if (!body) return null
  return (
    <div className="tile-markdown text-[12.5px] leading-snug text-foreground/85">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: textOnly,
          img: () => null,
          h1: ({ children }) => <p className="my-1 font-semibold text-foreground">{children}</p>,
          h2: ({ children }) => <p className="my-1 font-semibold text-foreground">{children}</p>,
          h3: ({ children }) => <p className="my-1 font-semibold text-foreground">{children}</p>,
          h4: ({ children }) => <p className="my-1 font-semibold text-foreground">{children}</p>,
          p: ({ children }) => <p className="my-1">{children}</p>,
          ul: ({ children }) => <ul className="my-1 list-disc pl-4">{children}</ul>,
          ol: ({ children }) => <ol className="my-1 list-decimal pl-4">{children}</ol>,
          li: ({ children }) => <li className="my-0.5">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="my-1 border-l-2 border-border pl-2 text-muted-foreground">{children}</blockquote>
          ),
          pre: ({ children }) => <pre className="my-1 overflow-hidden text-[11px]">{children}</pre>,
          code: ({ children }) => <code className="rounded bg-muted px-0.5 text-[11px]">{children}</code>,
          table: ({ children }) => (
            <table className="my-1 w-full border-collapse text-[11px]">{children}</table>
          ),
          th: ({ children }) => <th className="border border-border px-1 py-0.5 text-left font-medium">{children}</th>,
          td: ({ children }) => <td className="border border-border px-1 py-0.5">{children}</td>,
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  )
}
